/**
 * AccountDetailsCard — Inline-editable Account/Contact Details card.
 * Props: account, canEditAccount, onAccountUpdate, users, usersMap
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { CheckIcon, ChevronsUpDown, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { crmAPI } from '../../../services/api';
import { TIMEZONES } from '../../../utils/timezoneConstants';

const ratingColors = {
  A: 'bg-green-100 text-green-800', B: 'bg-blue-100 text-blue-800',
  C: 'bg-yellow-100 text-yellow-800', D: 'bg-orange-100 text-orange-800',
  F: 'bg-red-100 text-red-800', unrated: 'bg-gray-100 text-gray-600',
};

const BLANK_FORM = {
  crm_type: 'organization',
  organization_name: '', account_manager_id: '', account_admin_id: '',
  is_active: true, account_rating: '', additional_identifier: '', timezone: '',
  organization_dba: '', organization_website: '', organization_industry_role: '',
  person_prefix: '', person_first_name: '', person_last_name: '',
  person_suffix: '', person_preferred_name: '', person_salutation: '',
  person_industry_role: '', person_gender: '',
  person_birthday_month: '', person_birthday_day: '',
  person_spouse_children_names: '', person_alma_mater: '',
  person_hobbies: '', person_clothing_size: '', person_sports_teams: '',
  person_cuisine_preferences: '', person_drink_preferences: '', person_other_notes: '',
};

const AccountDetailsCard = ({ account, canEditAccount, onAccountUpdate, users = [], usersMap = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);

  useEffect(() => {
    if (account) {
      setForm({
        crm_type: account.crm_type || 'organization',
        organization_name: account.organization_name || '',
        account_manager_id: account.account_manager_id || '',
        account_admin_id: account.account_admin_id || '',
        is_active: account.is_active !== false && account.account_active !== false,
        account_rating: (account.account_rating && account.account_rating !== 'unrated') ? account.account_rating : (account.rating && account.rating !== 'unrated') ? account.rating : '',
        additional_identifier: account.additional_identifier || '',
        timezone: account.timezone || '',
        organization_dba: account.organization_dba || '',
        organization_website: account.organization_website || '',
        organization_industry_role: account.organization_industry_role || '',
        person_prefix: account.person_prefix || '',
        person_first_name: account.person_first_name || '',
        person_last_name: account.person_last_name || '',
        person_suffix: account.person_suffix || '',
        person_preferred_name: account.person_preferred_name || '',
        person_salutation: account.person_salutation || '',
        person_industry_role: account.person_industry_role || '',
        person_gender: account.person_gender || '',
        person_birthday_month: account.person_birthday ? account.person_birthday.split('-')[0] || '' : '',
        person_birthday_day: account.person_birthday ? account.person_birthday.split('-')[1] || '' : '',
        person_spouse_children_names: account.person_spouse_children_names || '',
        person_alma_mater: account.person_alma_mater || '',
        person_hobbies: account.person_hobbies || '',
        person_clothing_size: account.person_clothing_size || '',
        person_sports_teams: account.person_sports_teams || '',
        person_cuisine_preferences: account.person_cuisine_preferences || '',
        person_drink_preferences: account.person_drink_preferences || '',
        person_other_notes: account.person_other_notes || '',
      });
    }
  }, [account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelEdit = () => {
    if (account) {
      setForm({
        crm_type: account.crm_type || 'organization',
        organization_name: account.organization_name || '',
        account_manager_id: account.account_manager_id || '',
        account_admin_id: account.account_admin_id || '',
        is_active: account.is_active !== false && account.account_active !== false,
        account_rating: (account.account_rating && account.account_rating !== 'unrated') ? account.account_rating : '',
        additional_identifier: account.additional_identifier || '',
        timezone: account.timezone || '',
        organization_dba: account.organization_dba || '',
        organization_website: account.organization_website || '',
        organization_industry_role: account.organization_industry_role || '',
        person_prefix: account.person_prefix || '',
        person_first_name: account.person_first_name || '',
        person_last_name: account.person_last_name || '',
        person_suffix: account.person_suffix || '',
        person_preferred_name: account.person_preferred_name || '',
        person_salutation: account.person_salutation || '',
        person_industry_role: account.person_industry_role || '',
        person_gender: account.person_gender || '',
        person_birthday_month: account.person_birthday ? account.person_birthday.split('-')[0] || '' : '',
        person_birthday_day: account.person_birthday ? account.person_birthday.split('-')[1] || '' : '',
        person_spouse_children_names: account.person_spouse_children_names || '',
        person_alma_mater: account.person_alma_mater || '',
        person_hobbies: account.person_hobbies || '',
        person_clothing_size: account.person_clothing_size || '',
        person_sports_teams: account.person_sports_teams || '',
        person_cuisine_preferences: account.person_cuisine_preferences || '',
        person_drink_preferences: account.person_drink_preferences || '',
        person_other_notes: account.person_other_notes || '',
      });
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = { ...form };
      if (dataToSave.person_birthday_month && dataToSave.person_birthday_day) {
        dataToSave.person_birthday = `${dataToSave.person_birthday_month}-${dataToSave.person_birthday_day}`;
      } else {
        dataToSave.person_birthday = '';
      }
      delete dataToSave.person_birthday_month;
      delete dataToSave.person_birthday_day;
      await crmAPI.update(account.id, dataToSave);
      toast.success('Details updated');
      setIsEditing(false);
      if (onAccountUpdate) onAccountUpdate();
    } catch {
      toast.error('Failed to save details');
    } finally {
      setSaving(false);
    }
  };

  return (
<Card>
  <CardHeader className="flex flex-row items-center justify-between pb-2">
    <CardTitle>Details</CardTitle>
    {canEditAccount && !isEditing && (
      <Button variant="ghost" size="icon" onClick={async () => {
        setForm({
          crm_type: account.crm_type || 'organization',
          organization_name: account.organization_name || '',
          account_manager_id: account.account_manager_id || '',
          account_admin_id: account.account_admin_id || '',
          is_active: account.is_active !== false && account.account_active !== false,
          account_rating: (account.account_rating && account.account_rating !== 'unrated') ? account.account_rating : (account.rating && account.rating !== 'unrated') ? account.rating : '',
          additional_identifier: account.additional_identifier || '',
          timezone: account.timezone || '',
          organization_dba: account.organization_dba || '',
          organization_website: account.organization_website || '',
          organization_industry_role: account.organization_industry_role || '',
          person_prefix: account.person_prefix || '',
          person_first_name: account.person_first_name || '',
          person_last_name: account.person_last_name || '',
          person_suffix: account.person_suffix || '',
          person_preferred_name: account.person_preferred_name || '',
          person_salutation: account.person_salutation || '',
          person_industry_role: account.person_industry_role || '',
          person_birthday_month: account.person_birthday?.substring(0, 2) || '',
          person_birthday_day: account.person_birthday?.substring(2, 4) || '',
          person_spouse_children_names: account.person_spouse_children_names || '',
          person_alma_mater: account.person_alma_mater || '',
          person_hobbies: account.person_hobbies || '',
          person_clothing_size: account.person_clothing_size || '',
          person_sports_teams: account.person_sports_teams || '',
          person_cuisine_preferences: account.person_cuisine_preferences || '',
          person_drink_preferences: account.person_drink_preferences || '',
          person_other_notes: account.person_other_notes || '',
        });
        setIsEditing(true);
      }} data-testid="edit-details-btn">
        <Pencil className="w-4 h-4" />
      </Button>
    )}
  </CardHeader>
  <CardContent className="space-y-4">
    {/* Account Management Fields - Only for Accounts */}
    {account.crm_class === 'account' && (
      <div>
        <h4 className="text-sm font-medium mb-2">{account.is_tenant_managed ? 'Syndicate Management' : 'Account Management'}</h4>
        {isEditing ? (
        <div className="grid grid-cols-2 gap-4">
          {!account.is_tenant_managed && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Rating</Label>
            <Select value={form.account_rating || ''} onValueChange={(val) => setForm({...form, account_rating: val})}>
              <SelectTrigger data-testid="rating-select">
                <SelectValue placeholder="Select rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unrated">Unrated</SelectItem>
                <SelectItem value="A">A - Top Priority</SelectItem>
                <SelectItem value="B">B - High Priority</SelectItem>
                <SelectItem value="C">C - Normal</SelectItem>
                <SelectItem value="D">D - Low Priority</SelectItem>
              </SelectContent>
            </Select>
          </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Active</Label>
            <div className="flex items-center h-10">
              <Switch 
                checked={form.is_active} 
                onCheckedChange={(checked) => setForm({...form, is_active: checked})}
                data-testid="active-switch"
              />
              <span className="ml-2 text-sm">{form.is_active ? 'Yes' : 'No'}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Acct Manager</Label>
            <Select value={form.account_manager_id || 'none'} onValueChange={(val) => setForm({...form, account_manager_id: val === 'none' ? '' : val})}>
              <SelectTrigger data-testid="manager-select">
                <SelectValue placeholder="Select manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Acct Admin</Label>
            <Select value={form.account_admin_id || 'none'} onValueChange={(val) => setForm({...form, account_admin_id: val === 'none' ? '' : val})}>
              <SelectTrigger data-testid="admin-select">
                <SelectValue placeholder="Select admin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
          {/* Rating - hidden for managed syndicates */}
          {!account.is_tenant_managed && (
            <>
              <dt className="text-muted-foreground">Rating</dt>
              <dd>
                <Badge className={`${ratingColors[account.rating || account.account_rating || 'unrated']} font-semibold`}>
                  {(account.rating || account.account_rating || 'N/A').toUpperCase()}
                </Badge>
              </dd>
            </>
          )}
          {/* Active - always show */}
          <dt className="text-muted-foreground">Active</dt>
          <dd>
            {account.is_active !== false && account.account_active !== false ? (
              <Badge className="badge-active">Active</Badge>
            ) : (
              <Badge variant="outline">Inactive</Badge>
            )}
          </dd>
          {/* Acct Manager - only show if has value and is account */}
          {usersMap[account.account_manager_id] && account.crm_class === 'account' && (
            <>
              <dt className="text-muted-foreground">Acct Manager</dt>
              <dd>{usersMap[account.account_manager_id]}</dd>
            </>
          )}
          {/* Acct Admin - only show if has value and is account */}
          {usersMap[account.account_admin_id] && account.crm_class === 'account' && (
            <>
              <dt className="text-muted-foreground">Acct Admin</dt>
              <dd>{usersMap[account.account_admin_id]}</dd>
            </>
          )}
        </dl>
      )}
    </div>
    )}

    {/* Basic Information */}
    <div className="pt-4 border-t">
      <h4 className="text-sm font-medium mb-2">Basic Information</h4>
      {isEditing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={form.crm_type} onValueChange={(val) => setForm({...form, crm_type: val})}>
                <SelectTrigger data-testid="type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="organization">Organization</SelectItem>
                  <SelectItem value="person">Person</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Additional Identifier</Label>
              <Input 
                value={form.additional_identifier} 
                onChange={(e) => setForm({...form, additional_identifier: e.target.value})}
                placeholder="Optional identifier"
                data-testid="additional-identifier-input"
              />
            </div>
          </div>
          
          {form.crm_type === 'organization' ? (
            <>
              {/* Organization Name */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Organization Name</Label>
                <Input
                  value={form.organization_name}
                  onChange={(e) => setForm({...form, organization_name: e.target.value})}
                  placeholder="Organization name"
                  data-testid="organization-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Timezone</Label>
                  <Select value={form.timezone || 'none'} onValueChange={(val) => setForm({...form, timezone: val === 'none' ? '' : val})}>
                    <SelectTrigger data-testid="timezone-select">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Industry Role</Label>
                  <Select value={form.organization_industry_role || ''} onValueChange={(val) => setForm({...form, organization_industry_role: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="farm">Farm</SelectItem>
                      <SelectItem value="farm_boarding">Boarding Farm</SelectItem>
                      <SelectItem value="veterinary_care">Veterinary Practice</SelectItem>
                      <SelectItem value="consignment">Consignment</SelectItem>
                      <SelectItem value="stable">Stable</SelectItem>
                      <SelectItem value="syndicate">Syndicate</SelectItem>
                      <SelectItem value="transport">Transport</SelectItem>
                      <SelectItem value="insurance">Insurance</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">DBA</Label>
                  <Input 
                    value={form.organization_dba} 
                    onChange={(e) => setForm({...form, organization_dba: e.target.value})}
                    data-testid="dba-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Website</Label>
                  <Input 
                    value={form.organization_website} 
                    onChange={(e) => setForm({...form, organization_website: e.target.value})}
                    data-testid="website-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Salutation</Label>
                  <Input
                    value={form.person_salutation}
                    onChange={(e) => setForm({...form, person_salutation: e.target.value})}
                    placeholder="Dear John, Hi Jane, etc."
                    data-testid="org-salutation-input"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Timezone</Label>
                  <Select value={form.timezone || 'none'} onValueChange={(val) => setForm({...form, timezone: val === 'none' ? '' : val})}>
                    <SelectTrigger data-testid="timezone-select">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Industry Role</Label>
                  <Select value={form.person_industry_role || ''} onValueChange={(val) => setForm({...form, person_industry_role: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="trainer">Trainer</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="farm_manager">Farm Manager</SelectItem>
                      <SelectItem value="veterinarian">Veterinarian</SelectItem>
                      <SelectItem value="consignor">Consignor</SelectItem>
                      <SelectItem value="syndicate_member">Syndicate Member</SelectItem>
                      <SelectItem value="insurance">Insurance</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Prefix</Label>
                  <Input 
                    value={form.person_prefix} 
                    onChange={(e) => setForm({...form, person_prefix: e.target.value})}
                    placeholder="Mr., Mrs., Dr., etc."
                    data-testid="prefix-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">First Name</Label>
                  <Input 
                    value={form.person_first_name} 
                    onChange={(e) => setForm({...form, person_first_name: e.target.value})}
                    placeholder="First name"
                    data-testid="first-name-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Last Name</Label>
                  <Input 
                    value={form.person_last_name} 
                    onChange={(e) => setForm({...form, person_last_name: e.target.value})}
                    placeholder="Last name"
                    data-testid="last-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Suffix</Label>
                  <Input 
                    value={form.person_suffix} 
                    onChange={(e) => setForm({...form, person_suffix: e.target.value})}
                    placeholder="Jr., Sr., III, etc."
                    data-testid="suffix-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Preferred Name</Label>
                  <Input 
                    value={form.person_preferred_name} 
                    onChange={(e) => setForm({...form, person_preferred_name: e.target.value})}
                    placeholder="Nickname or preferred name"
                    data-testid="preferred-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Salutation</Label>
                  <Input 
                    value={form.person_salutation} 
                    onChange={(e) => setForm({...form, person_salutation: e.target.value})}
                    placeholder="Dear John, Hi Jane, etc."
                    data-testid="salutation-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Gender</Label>
                  <Select value={form.person_gender || ''} onValueChange={(val) => setForm({...form, person_gender: val})}>
                    <SelectTrigger data-testid="gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="non_binary">Non-Binary</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
          <dt className="text-muted-foreground">Type</dt>
          <dd className="capitalize font-medium">{account.crm_type}</dd>
          
          {account.additional_identifier && (
            <>
              <dt className="text-muted-foreground">Additional Identifier</dt>
              <dd>{account.additional_identifier}</dd>
            </>
          )}
          
          {account.timezone && (
            <>
              <dt className="text-muted-foreground">Timezone</dt>
              <dd>{account.timezone}</dd>
            </>
          )}
          
          {account.crm_type === 'organization' ? (
            <>
              <dt className="text-muted-foreground">Organization Name</dt>
              <dd className="font-medium">{account.organization_name || account.system_name || '-'}</dd>
              {account.organization_dba && (
                <>
                  <dt className="text-muted-foreground">DBA</dt>
                  <dd>{account.organization_dba}</dd>
                </>
              )}
              {account.organization_website && (
                <>
                  <dt className="text-muted-foreground">Website</dt>
                  <dd><a href={account.organization_website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{account.organization_website}</a></dd>
                </>
              )}
              {account.person_salutation && (
                <>
                  <dt className="text-muted-foreground">Salutation</dt>
                  <dd>{account.person_salutation}</dd>
                </>
              )}
            </>
          ) : (
            <>
              {account.person_prefix && (
                <>
                  <dt className="text-muted-foreground">Prefix</dt>
                  <dd>{account.person_prefix}</dd>
                </>
              )}
              {account.person_first_name && (
                <>
                  <dt className="text-muted-foreground">First Name</dt>
                  <dd>{account.person_first_name}</dd>
                </>
              )}
              {account.person_last_name && (
                <>
                  <dt className="text-muted-foreground">Last Name</dt>
                  <dd>{account.person_last_name}</dd>
                </>
              )}
              {account.person_suffix && (
                <>
                  <dt className="text-muted-foreground">Suffix</dt>
                  <dd>{account.person_suffix}</dd>
                </>
              )}
              {account.person_preferred_name && (
                <>
                  <dt className="text-muted-foreground">Preferred Name</dt>
                  <dd>{account.person_preferred_name}</dd>
                </>
              )}
              {account.person_salutation && (
                <>
                  <dt className="text-muted-foreground">Salutation</dt>
                  <dd>{account.person_salutation}</dd>
                </>
              )}
              {account.person_gender && (
                <>
                  <dt className="text-muted-foreground">Gender</dt>
                  <dd className="capitalize">{account.person_gender.replace('_', ' ')}</dd>
                </>
              )}
            </>
          )}
        </dl>
      )}
    </div>

    {/* Personal Details (only for person type) */}
    {(isEditing ? form.crm_type === 'person' : account.crm_type === 'person') && (
      <div className="pt-4 border-t">
        <h4 className="text-sm font-medium mb-2">Personal Details</h4>
        {isEditing ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Birthday</Label>
              <div className="flex gap-2">
                <Select value={form.person_birthday_month || ''} onValueChange={(val) => setForm({...form, person_birthday_month: val})}>
                  <SelectTrigger className="w-[120px]" data-testid="birthday-month-select">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="01">January</SelectItem>
                    <SelectItem value="02">February</SelectItem>
                    <SelectItem value="03">March</SelectItem>
                    <SelectItem value="04">April</SelectItem>
                    <SelectItem value="05">May</SelectItem>
                    <SelectItem value="06">June</SelectItem>
                    <SelectItem value="07">July</SelectItem>
                    <SelectItem value="08">August</SelectItem>
                    <SelectItem value="09">September</SelectItem>
                    <SelectItem value="10">October</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">December</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={form.person_birthday_day || ''} onValueChange={(val) => setForm({...form, person_birthday_day: val})}>
                  <SelectTrigger className="w-[80px]" data-testid="birthday-day-select">
                    <SelectValue placeholder="Day" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <SelectItem key={day} value={day.toString().padStart(2, '0')}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Spouse/Children</Label>
              <Input 
                value={form.person_spouse_children_names} 
                onChange={(e) => setForm({...form, person_spouse_children_names: e.target.value})}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Alma Mater</Label>
              <Input 
                value={form.person_alma_mater} 
                onChange={(e) => setForm({...form, person_alma_mater: e.target.value})}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Hobbies</Label>
              <Input 
                value={form.person_hobbies} 
                onChange={(e) => setForm({...form, person_hobbies: e.target.value})}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Clothing Size</Label>
              <Input 
                value={form.person_clothing_size} 
                onChange={(e) => setForm({...form, person_clothing_size: e.target.value})}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Sports Teams</Label>
              <Input 
                value={form.person_sports_teams} 
                onChange={(e) => setForm({...form, person_sports_teams: e.target.value})}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Cuisine Prefs</Label>
              <Input 
                value={form.person_cuisine_preferences} 
                onChange={(e) => setForm({...form, person_cuisine_preferences: e.target.value})}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Drink Prefs</Label>
              <Input 
                value={form.person_drink_preferences} 
                onChange={(e) => setForm({...form, person_drink_preferences: e.target.value})}
              />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs text-muted-foreground">Other Notes</Label>
              <Input 
                value={form.person_other_notes} 
                onChange={(e) => setForm({...form, person_other_notes: e.target.value})}
              />
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
            {account.person_birthday && (
              <>
                <dt className="text-muted-foreground">Birthday</dt>
                <dd>{account.person_birthday}</dd>
              </>
            )}
            {account.person_spouse_children_names && (
              <>
                <dt className="text-muted-foreground">Spouse/Children</dt>
                <dd>{account.person_spouse_children_names}</dd>
              </>
            )}
            {account.person_alma_mater && (
              <>
                <dt className="text-muted-foreground">Alma Mater</dt>
                <dd>{account.person_alma_mater}</dd>
              </>
            )}
            {account.person_hobbies && (
              <>
                <dt className="text-muted-foreground">Hobbies</dt>
                <dd>{account.person_hobbies}</dd>
              </>
            )}
            {account.person_clothing_size && (
              <>
                <dt className="text-muted-foreground">Clothing Size</dt>
                <dd>{account.person_clothing_size}</dd>
              </>
            )}
            {account.person_sports_teams && (
              <>
                <dt className="text-muted-foreground">Sports Teams</dt>
                <dd>{account.person_sports_teams}</dd>
              </>
            )}
            {account.person_cuisine_preferences && (
              <>
                <dt className="text-muted-foreground">Cuisine Prefs</dt>
                <dd>{account.person_cuisine_preferences}</dd>
              </>
            )}
            {account.person_drink_preferences && (
              <>
                <dt className="text-muted-foreground">Drink Prefs</dt>
                <dd>{account.person_drink_preferences}</dd>
              </>
            )}
            {account.person_other_notes && (
              <>
                <dt className="text-muted-foreground">Other Notes</dt>
                <dd>{account.person_other_notes}</dd>
              </>
            )}
          </dl>
        )}
      </div>
    )}
    {isEditing && (
      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
        <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving} data-testid="cancel-details-btn">Cancel</Button>
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-details-btn">{saving ? 'Saving...' : 'Save'}</Button>
      </div>
    )}
  </CardContent>
</Card>

  );
};

export default AccountDetailsCard;
